import type { CSSProperties, ExtractPropTypes, ExtractPublicPropTypes, PropType, VNode } from 'vue';
import type Table from './table.vue';

type TRowClassName = (data: { row: any; rowIndex: number }) => string;
type TRowStyle = (data: { row: any; rowIndex: number }) => CSSProperties;
type TCellClassName = (data: { row: any; column: any; rowIndex: number; columnIndex: number }) => string;
type TCellStyle = (data: { row: any; column: any; rowIndex: number; columnIndex: number }) => CSSProperties;

export interface IElTableColumnProps {
  type?: 'default' | 'selection' | 'index' | 'expand';
  index?: (index: number) => number;
  label?: string;
  rowKey?: string;
  columnKey?: string;
  prop?: string;
  width?: string | number;
  minWidth?: string | number;
  fixed?: boolean | 'left' | 'right';
  sortable?: boolean | string;
  sortMethod?: <T = any>(a: T, b: T) => number;
  sortBy?: string | string[] | ((row: any, index: number) => string);
  sortOrders?: ('ascending' | 'descending' | null)[];
  resizable?: boolean;
  formatter?: (row: any, column: any, cellValue: any, index: number) => VNode | string;
  showOverflowTooltip?: boolean;
  align?: 'left' | 'center' | 'right';
  headerAlign?: 'left' | 'center' | 'right';
  className?: string;
  labelClassName?: string;
  selectable?: (row: any, index: number) => boolean;
  reserveSelection?: boolean;
  filters?: Array<{ text: string; value: string }>;
  filterPlacement?:
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'left'
    | 'left-start'
    | 'left-end'
    | 'right'
    | 'right-start'
    | 'right-end';
  filterMultiple?: boolean;
  filterMethod?: (value: any, row: any, column: any) => void;
  filteredValue?: string[];
}

export type TableColumnProps = IElTableColumnProps & {
  slot?: string;
};

export interface IElPaginationProps {
  small?: boolean;
  background?: boolean;
  pageSize?: number;
  defaultPageSize?: number;
  total?: number;
  pageCount?: number;
  pagerCount?: number;
  currentPage?: number;
  defaultCurrentPage?: number;
  layout?: string;
  pageSizes?: number[];
  popperClass?: string;
  prevText?: string;
  prevIcon?: string;
  nextText?: string;
  nextIcon?: string;
  disabled?: boolean;
  hideOnSinglePage?: boolean;
}

export const TableProps = {
  data: {
    type: Array as PropType<any[]>,
    default: () => [],
  },
  height: [String, Number],
  maxHeight: [String, Number],
  stripe: {
    type: Boolean,
    default: true,
  },
  border: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String as PropType<'' | 'large' | 'default' | 'small'>,
    default: '',
  },
  fit: {
    type: Boolean,
    default: true,
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
  highlightCurrentRow: {
    type: Boolean,
    default: false,
  },
  currentRowKey: [String, Number],
  rowClassName: {
    type: [Function, String] as PropType<string | TRowClassName>,
    default: () => '',
  },
  rowStyle: {
    type: [Function, Object] as PropType<TRowStyle | CSSProperties>,
    default: () => ({}),
  },
  cellClassName: {
    type: [Function, String] as PropType<string | TCellClassName>,
    default: () => '',
  },
  cellStyle: {
    type: [Function, Object] as PropType<TCellStyle | CSSProperties>,
    default: () => ({}),
  },
  headerRowClassName: {
    type: [Function, String] as PropType<string | TRowClassName>,
    default: () => '',
  },
  headerRowStyle: {
    type: [Function, Object] as PropType<TRowStyle | CSSProperties>,
    default: () => ({}),
  },
  headerCellClassName: {
    type: [Function, String] as PropType<string | TCellClassName>,
    default: () => '',
  },
  headerCellStyle: {
    type: [Function, Object] as PropType<TCellStyle | CSSProperties>,
    default: () => ({}),
  },
  rowKey: {
    type: [Function, String] as PropType<string | ((row: any) => string)>,
    default: 'id',
  },
  emptyText: {
    type: String,
    default: '暂无数据',
  },
  defaultExpandAll: {
    type: Boolean,
    default: false,
  },
  expandRowKeys: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  defaultSort: {
    type: Object,
    default: () => ({}),
  },
  tooltipEffect: {
    type: String as PropType<'dark' | 'light'>,
    default: 'dark',
  },
  tooltipOptions: Object,
  showSummary: Boolean,
  sumText: String,
  summaryMethod: Function,
  spanMethod: Function,
  selectOnIndeterminate: Boolean,
  indent: Number,
  lazy: Boolean,
  loading: {
    type: Boolean,
    default: false,
  },
  load: Function,
  treeProps: Object,
  tableLayout: {
    type: String as PropType<'fixed' | 'auto'>,
    default: 'fixed',
  },
  scrollbarAlwaysOn: {
    type: Boolean,
    default: false,
  },
  showOverflowTooltip: {
    type: Boolean,
    default: true,
  },
  /**
   * 自定义属性
   */
  // 表格列配置
  columns: {
    type: Array as PropType<TableColumnProps[]>,
    default: () => [],
  },
  // 分页器配置
  pagination: {
    type: Object as PropType<IElPaginationProps>,
    default: () => ({}),
  },
};

export type TablePropsType = ExtractPropTypes<typeof TableProps>;
export type TablePublicPropsType = ExtractPublicPropTypes<typeof TableProps>;
export type TableInstance = InstanceType<typeof Table>;
